import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable } from "rxjs";
import { EstudioMedicoPaciente } from "app/models/estudioMedicoPaciente.interface";
import { GlobalVariable } from "app/global/global";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { SpinnerService } from "./spinner.service";
import { CornerstoneService } from "./cornerstone.service";
import * as cornerstone from "cornerstone-core";
import { DatePipe } from "@angular/common";
import { ArchivoService } from "./archivo.service";
import { saveAs } from "file-saver";

export interface requestObjectEstudioMedicoPaciente {
  idPaciente?: string;
  idMedico?: string;
  incluirDatosAdicionales?: boolean;
  incluirCentrosMedicos?: boolean;
  incluirEstudiosMedicos?: boolean;
  incluirMedicos?: boolean;
  incluirPlantillasEstudiosMedicos?: boolean;
  incluirPacientes?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class EstudioMedicoPacienteService {
  url: string = GlobalVariable.BASE_API_URL + "api/estudio-medico-paciente/";

  constructor(
    private http: HttpClient,
    private spinnerService: SpinnerService,
    private cornerstoneService: CornerstoneService,
    private datePipe: DatePipe,
    private archivoService: ArchivoService
  ) {}

  listar(reqParams: requestObjectEstudioMedicoPaciente): Observable<any> {
    let params = new HttpParams().set("incluirDatosAdicionales", "1");

    const listarUrl =
      GlobalVariable.BASE_API_URL + "api/estudio-medico-paciente";

    const paramMappings = {
      idPaciente: "idPaciente",
      idMedico: "idMedico",
      incluirDatosAdicionales: "incluirDatosAdicionales",
      incluirCentrosMedicos: "incluirCentrosMedicos",
      incluirEstudiosMedicos: "incluirEstudiosMedicos",
      incluirMedicos: "incluirMedicos",
      incluirPacientes: "incluirPacientes",
      incluirPlantillasEstudiosMedicos: "incluirPlantillasEstudiosMedicos",
    };

    Object.keys(paramMappings).forEach((key) => {
      if (reqParams[key]) {
        params = params.append(paramMappings[key], reqParams[key]);
      }
    });

    return this.http.get(listarUrl, { params });
  }

  crear(listFile: File[], estudioMedicoPaciente: any, descripcion: string[]) {
    const formData = new FormData();
    const headers = new HttpHeaders();
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");
    formData.append("idPaciente", estudioMedicoPaciente.idPaciente);
    formData.append("medico", estudioMedicoPaciente.medico);
    formData.append("estudioMedico", estudioMedicoPaciente.estudioMedico);
    formData.append("fecha", estudioMedicoPaciente.fecha);
    for (let i = 0; i < listFile.length; i++) {
      formData.append("imagenes[" + i + "]", listFile[i]);
      formData.append("descripcion" + i, descripcion[i]);
    }
    formData.append("centroMedico", estudioMedicoPaciente.centroMedico);
    formData.append(
      "observaciones",
      (estudioMedicoPaciente.observaciones || "").trim()
    );
    return this.http.post(this.url + "crear", formData, { headers: headers });
  }

  borrar(estudioMedicoPaciente: EstudioMedicoPaciente): Observable<any> {
    return this.http.delete(this.url + "borrar/" + estudioMedicoPaciente.id);
  }

  //
  mostrar(id): Observable<any> {
    return this.http.get(this.url + "mostrar/" + id);
  }

  //
  estudioMedicoPacienteEditar(id, estudioId): Observable<any> {
    return this.http.get(this.url + "mostrar/" + id + "/" + estudioId);
  }

  editar(
    listFile: any,
    estudioMedicoPaciente: any,
    descripcion: string[]
  ): Observable<any> {
    const formData = new FormData();
    const headers = new HttpHeaders();
    headers.append("Content-Type", "multipart/form-data");
    headers.append("Accept", "application/json");
    formData.append("id", estudioMedicoPaciente.id);
    formData.append("medico", estudioMedicoPaciente.medico);
    formData.append("estudioMedico", estudioMedicoPaciente.estudioMedico);
    if (listFile) {
      for (let i = 0; i < listFile.length; i++) {
        if (!listFile[i]?.id) {
          formData.append("imagenes[" + i + "]", listFile[i]);
          formData.append("descripcion" + i, descripcion[i]);
        } else {
          formData.append("imagenesId[]", listFile[i]?.id);
        }
      }
    }
    formData.append("fecha", estudioMedicoPaciente.fecha);
    formData.append("centroMedico", estudioMedicoPaciente.centroMedico);
    formData.append(
      "observaciones",
      estudioMedicoPaciente.observaciones.trim()
    );
    return this.http.post(this.url + "editar", formData, { headers: headers });
  }

  mostrarDetalle(id, idMedico = null): Observable<any> {
    let urlDetalle = idMedico
      ? "detalle/" + id + "/" + idMedico
      : "detalle/" + id;

    return this.http.get(this.url + urlDetalle);
  }

  cantidad() {
    return this.http.get(this.url + "cantidad");
  }

  abrirVisorImagenes(id, renovar = false): Observable<any> {
    return this.http.post(
      GlobalVariable.BASE_API_URL + "api/orthanc/visor/abrir/" + id,
      renovar ? { renovar: true } : {}
    );
  }

  compartirImagenes(id: number, destination: string): Observable<any> {
    return this.http.post(
      GlobalVariable.BASE_API_URL + "api/imagenes/compartir/" + id,
      { destination }
    );
  }

  listarEstudiosMedicosPaciente(): Observable<any> {
    return this.http.get(this.url + "listar-estudios-medicos");
  }

  async descargarPDFEstudioMedico(
    estudioMedicoPaciente,
    imagenesArray = [],
    odontogramaEl: HTMLElement | null = null,
    perioElDirecto: HTMLElement | null = null
  ) {
    const dateFormatRegex = /^\d{2}\/\d{2}\/\d{4}$/;
    let formatedDate = estudioMedicoPaciente.fecha;

    if (!dateFormatRegex.test(estudioMedicoPaciente.fecha)) {
      formatedDate = this.datePipe.transform(
        estudioMedicoPaciente.fecha,
        "dd/MM/yyyy"
      );
    }

    // Crear contenedor HTML temporal
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = `
    <div style="display: flex; flex-direction: column; min-height: ${
      odontogramaEl || perioElDirecto ? "auto" : "297mm"
    }; font-family: Arial, sans-serif; padding: 10mm;">
      <header style="display:flex; justify-content: space-between; text-align: center; margin-bottom: 10mm; border-bottom: 1px solid #ccc;">
        <div id="logo-placeholder" style="height: 50px; width: 50px;"></div>
        <div>
          <h2 style="margin: 5px 0;">${estudioMedicoPaciente.nombreCM}</h2>
          <p>${estudioMedicoPaciente.direccionCM} | Tel: ${
      estudioMedicoPaciente.telefonoCMPais
    } ${estudioMedicoPaciente.telefonoCMCaract} ${
      estudioMedicoPaciente.telefonoCM
    } | Mail: ${estudioMedicoPaciente.emailCM}</p>
        </div>
        <img src="assets/img/logo.png" alt="${
          estudioMedicoPaciente.nombreCM
        }" style="height: 50px;">
      </header>
      <section style="margin-bottom: 10px;">
        <p><b>Fecha:</b> ${formatedDate}</p>
      </section>
      <hr style="border: 1px solid #ccc; margin: 10px 0;">
      <section style="display: flex; justify-content: space-between; border-bottom: 1px solid #ccc; padding-bottom: 10px;">
        <div style="width: 50%;">
          <p><b>Apellido y Nombre:</b> ${estudioMedicoPaciente.nombreP}</p>
          <p><b>Doctor:</b> ${estudioMedicoPaciente.nombreM}</p>
          <p><b>Estudio:</b> ${estudioMedicoPaciente.nombreEM}</p>
        </div>
        <div style="width: 50%; text-align: right;">
          <p><b>OS:</b> ${estudioMedicoPaciente.nombreOS}</p>
        </div>
      </section>
      <div style="padding-bottom: 5px; margin-top: 15px;"></div>
      <div>${estudioMedicoPaciente.observaciones || ""}</div>
    </div>
  `;
    tempDiv.style.position = "absolute";
    tempDiv.style.top = "-9999px";
    document.body.appendChild(tempDiv);

    this.spinnerService.mostrarSpinner();

    try {
      // Crear PDF vacío
      const pdf = new jsPDF("p", "mm", "a4", true);

      // Renderizar HTML inicial en canvas
      const canvas = await html2canvas(tempDiv, { scale: 2, useCORS: true });
      document.body.removeChild(tempDiv);

      const pdfImgData = canvas.toDataURL("image/png");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = (canvas.height * pdfWidth) / canvas.width;
      pdf.addImage(pdfImgData, "PNG", 0, 0, pdfWidth, pdfHeight);

      // Vista del odontograma (si corresponde), debajo del contenido y centrada
      if (odontogramaEl) {
        const svgEl = odontogramaEl.querySelector("svg") as SVGSVGElement | null;
        const chartImg = svgEl ? await this.svgAImagen(svgEl) : null;

        const legendEl = odontogramaEl.querySelector(".leyenda") as HTMLElement | null;
        let legendData: { dataUrl: string; w: number; h: number } | null = null;
        if (legendEl && legendEl.getBoundingClientRect().width) {
          const lc = await html2canvas(legendEl, {
            scale: 2,
            useCORS: true,
            backgroundColor: "#ffffff",
          });
          const pxToMm = 0.2645833;
          legendData = {
            dataUrl: lc.toDataURL("image/png"),
            w: (lc.width / 2) * pxToMm,
            h: (lc.height / 2) * pxToMm,
          };
        }

        const pageHeight = pdf.internal.pageSize.getHeight();
        let oY = pdfHeight + 8;

        if (chartImg && chartImg.dataUrl) {
          const chartW = pdfWidth - 24;
          const chartH = (chartW * chartImg.h) / chartImg.w;
          if (oY + chartH > pageHeight) {
            pdf.addPage();
            oY = 12;
          }
          pdf.addImage(
            chartImg.dataUrl,
            "PNG",
            (pdfWidth - chartW) / 2,
            oY,
            chartW,
            chartH
          );
          oY += chartH + 4;
        }

        if (legendData) {
          const lW = Math.min(legendData.w, pdfWidth - 24);
          const lH = (lW * legendData.h) / legendData.w;
          if (oY + lH > pageHeight) {
            pdf.addPage();
            oY = 12;
          }
          pdf.addImage(
            legendData.dataUrl,
            "PNG",
            (pdfWidth - lW) / 2,
            oY,
            lW,
            lH
          );
        }
      }

      // Vista del periodontograma (si corresponde)
      const perioEl =
        perioElDirecto ||
        (odontogramaEl
          ? (odontogramaEl.querySelector("app-periodontograma-detalle") as HTMLElement | null)
          : null);
      if (perioEl && perioEl.getBoundingClientRect().width) {
        const perioCanvas = await html2canvas(perioEl, {
          scale: 2,
          useCORS: true,
          backgroundColor: "#ffffff",
        });
        const perioImg = perioCanvas.toDataURL("image/png");
        const margin = 10;
        const imgW = pdfWidth - margin * 2;
        const imgH = (perioCanvas.height * imgW) / perioCanvas.width;
        pdf.addPage();
        pdf.setFontSize(13);
        pdf.text("Periodontograma", margin, margin + 4);
        const maxH = pdf.internal.pageSize.getHeight() - margin * 2 - 10;
        pdf.addImage(perioImg, "PNG", margin, margin + 10, imgW, Math.min(imgH, maxH));
      }

      // Archivos extra (no imágenes)
      const archivosExtra: { nombre: string; blob: Blob }[] = [];

      // Procesar imágenes, DICOM y extras
      await this.descargarYProcesarImagenes(pdf, imagenesArray, archivosExtra);

      // Descargar el PDF principal
      const pdfBlob = pdf.output("blob");
      const pdfName = `${formatedDate}-${estudioMedicoPaciente.nombreEM}-${estudioMedicoPaciente.nombreP}.pdf`;
      saveAs(pdfBlob, pdfName);

      // Descargar archivos extra (uno por uno, al mismo nivel que el PDF)
      archivosExtra.forEach((file) => {
        saveAs(file.blob, file.nombre);
      });

      this.spinnerService.ocultarSpinner();
    } catch (error) {
      console.error("Error generando PDF:", error);
      this.spinnerService.ocultarSpinner();
    }
  }

  private svgAImagen(
    svg: SVGSVGElement,
    scale = 4
  ): Promise<{ dataUrl: string; w: number; h: number }> {
    const vb = svg.viewBox.baseVal;
    const rect = svg.getBoundingClientRect();
    const w = Math.round((vb && vb.width) || rect.width || 440);
    const h = Math.round((vb && vb.height) || rect.height || 160);
    const clone = svg.cloneNode(true) as SVGSVGElement;
    clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
    clone.setAttribute("width", String(w));
    clone.setAttribute("height", String(h));
    const style = document.createElementNS("http://www.w3.org/2000/svg", "style");
    style.textContent =
      ".zona{stroke:#5b6c8a;stroke-width:0.4;}.fdi-label{font-size:4.5px;font-weight:600;fill:#1a237e;font-family:Arial,sans-serif;}";
    clone.insertBefore(style, clone.firstChild);
    const xml = new XMLSerializer().serializeToString(clone);
    const src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(xml);
    return new Promise((resolve) => {
      const image = new Image();
      image.onload = () => {
        const canvas = document.createElement("canvas");
        canvas.width = w * scale;
        canvas.height = h * scale;
        const ctx = canvas.getContext("2d");
        if (ctx) {
          ctx.fillStyle = "#ffffff";
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
        }
        resolve({ dataUrl: canvas.toDataURL("image/png"), w, h });
      };
      image.onerror = () => resolve({ dataUrl: "", w, h });
      image.src = src;
    });
  }

  descargarYProcesarImagenes(
    pdf,
    imagenesArray: any[],
    archivosExtra: { nombre: string; blob: Blob }[]
  ) {
    const promesasImagenes = imagenesArray.map((imagen) => {
      return new Promise<void>((resolve) => {
        const esDicom = imagen.nombre.toLowerCase().endsWith(".dcm");
        const esImagen =
          imagen.nombre.toLowerCase().endsWith(".png") ||
          imagen.nombre.toLowerCase().endsWith(".jpg") ||
          imagen.nombre.toLowerCase().endsWith(".jpeg");

        if (!esDicom && !esImagen) {
          // Archivo extra → lo guardamos
          this.archivoService
            .getImagenProtegida(
              imagen.idPaciente,
              imagen.idEstudioMedico,
              imagen.nombre
            )
            .subscribe({
              next: (blob: Blob) => {
                archivosExtra.push({ nombre: imagen.nombre, blob });
                resolve();
              },
              error: (error) => {
                console.error(`Error descargando archivo extra: ${error}`);
                resolve();
              },
            });
          return;
        }

        if (esDicom) {
          return this.procesarImagenDicomConCornerstone(imagen, pdf)
            .then(() => resolve())
            .catch(() => resolve());
        }

        if (esImagen) {
          return this.descargarImagen(imagen)
            .then((url) => {
              const img = new Image();
              img.onload = () => {
                pdf.addPage();
                const imgWidth = pdf.internal.pageSize.getWidth() - 20;
                const imgHeight = (img.height * imgWidth) / img.width;
                pdf.setFontSize(10);
                if (imagen.descripcion) {
                  pdf.text(`Descripción: ${imagen.descripcion}`, 10, 16);
                }
                const formato = imagen.nombre.toLowerCase().endsWith(".png")
                  ? "PNG"
                  : "JPEG";
                pdf.addImage(img, formato, 10, 22, imgWidth, imgHeight);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = url;
            })
            .catch(() => resolve());
        }
      });
    });

    return Promise.all(promesasImagenes);
  }

  descargarImagen(imagen): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      // Usar el servicio para obtener la imagen protegida
      this.archivoService
        .getImagenProtegida(
          imagen.idPaciente,
          imagen.idEstudioMedico,
          imagen.nombre
        )
        .subscribe({
          next: (blob: Blob) => {
            // Convertir el blob a URL temporal
            const url = URL.createObjectURL(blob);
            resolve(url);
          },
          error: (error) => {
            console.error(
              `Error al descargar imagen protegida: ${error.message || error}`
            );
            reject(error);
          },
        });
    });
  }

  // Método para procesar imágenes DICOM usando CornerstoneService
  procesarImagenDicomConCornerstone(imagen, pdf) {
    const baseUrl = `${GlobalVariable.BASE_API_URL}storage/images/paciente`;
    const imageUrl = `${baseUrl}/${imagen.idPaciente}/${imagen.idEstudioMedico}/${imagen.nombre}`;

    return new Promise<void>((resolve, reject) => {
      // URL de la imagen DICOM
      const dicomUrl = imageUrl;

      // Crear un elemento temporal para renderizar la imagen DICOM
      const cornerDiv = document.createElement("div");
      cornerDiv.id = `temp-cornerstone-${imagen.id}`;
      cornerDiv.style.width = "512px";
      cornerDiv.style.height = "512px";
      cornerDiv.style.position = "absolute";
      cornerDiv.style.left = "-9999px";
      cornerDiv.style.top = "-9999px";
      cornerDiv.setAttribute("cornerstone", "");
      cornerDiv.className = "dcm";
      document.body.appendChild(cornerDiv);

      // Habilitar cornerstone en el elemento
      cornerstone.enable(cornerDiv);

      // Usar el servicio existente para cargar la imagen DICOM
      this.cornerstoneService.fetchDicomImage(imagen).subscribe({
        next: (image) => {
          // Renderizar la imagen en el div
          cornerstone.displayImage(cornerDiv, image);

          // Dar tiempo para que se renderice completamente
          setTimeout(() => {
            // Capturar el contenido renderizado
            html2canvas(cornerDiv, {
              useCORS: true,
              scale: 2,
              logging: false,
              allowTaint: true,
            })
              .then((canvas) => {
                // Limpiar recursos
                cornerstone.disable(cornerDiv);
                document.body.removeChild(cornerDiv);

                // Añadir nueva página al PDF
                pdf.addPage();

                // Convertir canvas a imagen para PDF
                const imgData = canvas.toDataURL("image/png");

                // Calcular dimensiones para PDF
                const pdfWidth = pdf.internal.pageSize.getWidth() - 20;
                const pdfHeight = (canvas.height * pdfWidth) / canvas.width;

                // Añadir información de la imagen DICOM
                pdf.setFontSize(12);
                if (imagen.descripcion) {
                  pdf.setFontSize(10);
                  pdf.text(
                    `Descripción: ${imagen.descripcion || "Sin descripción"}`,
                    10,
                    16
                  );
                }

                // Añadir la imagen procesada al PDF
                pdf.addImage(imgData, "PNG", 10, 22, pdfWidth, pdfHeight);

                resolve();
              })
              .catch((error) => {
                console.error("Error al capturar la imagen DICOM:", error);
                cornerstone.disable(cornerDiv);
                if (document.body.contains(cornerDiv)) {
                  document.body.removeChild(cornerDiv);
                }
                resolve(); // Continuamos con las siguientes imágenes
              });
          }, 1000); // Esperar 1 segundo para asegurar que la imagen se renderice completamente
        },
        error: (error) => {
          console.error(`Error al cargar la imagen DICOM: ${error}`);
          if (document.body.contains(cornerDiv)) {
            cornerstone.disable(cornerDiv);
            document.body.removeChild(cornerDiv);
          }
          resolve(); // Continuamos con las siguientes imágenes
        },
      });
    });
  }
}
